import { Paragraph, TextRun, TableRow, AlignmentType, PageOrientation } from "docx";
import * as m from "@app/paraglide/messages.js";
import {
    createHeaderCell,
    createDataCell,
    createEmptyCell,
    createRow,
    formatTimecode,
    buildContentWithTags,
} from "../helpers.js";
import convertToFormattedTextRuns from "../convertToFormattedTextRuns.js";

/**
 * Style 5A CCSL template.
 * 4 columns, landscape orientation, no shading.
 * Ignores spotting events entirely — treats dialogue events as the sole data source.
 * Uses only the main section with a page header (no head/metadata/footer sections).
 */
export default {
    id: "style5A",

    getDocumentConfig() {
        return {
            orientation: PageOrientation.LANDSCAPE,
            useSections: false,
            hasPageHeader: true
        };
    },

    /**
     * Returns the header row for the Style 5A template.
     * @returns {TableRow[]}
     */
    getHeaders() {
        return [new TableRow({
            tableHeader: true,
            children: [
                createHeaderCell(m.ccsl_export_header_scene_number(), AlignmentType.CENTER),
                createHeaderCell(m.ccsl_export_header_tc(), AlignmentType.CENTER),
                createHeaderCell(m.ccsl_export_header_shot_description(), AlignmentType.CENTER),
                createHeaderCell(m.ccsl_export_header_dialogue(), AlignmentType.CENTER),
            ]
        })];
    },

    /**
     * Builds table rows for a dialogue event, ignoring spotting events.
     * Style 5A doesn't care about spotting events — same output either way.
     * @param {object} event - The dialogue event
     * @param {number} index - Event index
     * @param {Array} _spottingEvents - Unused
     * @param {object} _spottingEventGroup - Unused
     * @param {object} project - Project state
     * @returns {TableRow[]}
     */
    buildDialogueRowWithSpotting(event, index, _spottingEvents, _spottingEventGroup, project) {
        return this._buildRow(event, index, project);
    },

    /**
     * Builds a table row for a dialogue event without spotting events.
     * @param {object} event - The dialogue event
     * @param {number} index - Event index
     * @param {object} project - Project state
     * @returns {TableRow[]}
     */
    buildDialogueRowWithoutSpotting(event, index, project) {
        return this._buildRow(event, index, project);
    },

    /**
     * Internal: builds the 4-cell row for a dialogue event.
     * @private
     */
    _buildRow(event, index, project) {
        return [createRow([
            createDataCell(
                new Paragraph({
                    children: [new TextRun({ text: (index + 1).toString() })],
                    alignment: AlignmentType.LEFT,
                })
            ),
            createDataCell(formatTimecode(event.start, project)),
            createDataCell(buildContentWithTags(event.tags, event.notes)),
            // Dialogue column: show formatted text when spotting events exist, empty otherwise
            createDataCell(
                event.text
                    ? new Paragraph({ children: convertToFormattedTextRuns(event.text) })
                    : new Paragraph("")
            ),
        ])];
    }
};
